import React from 'react';
import { Link } from 'react-router-dom';

function DoctorConsultation() {
  return (
    <article className="seo-page-container" style={{ 
      padding: 'clamp(15px, 4vw, 30px)', 
      background: '#fff', 
      margin: 'clamp(10px, 3vw, 20px)', 
      borderRadius: '4px', 
      textAlign: 'left', 
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      
      {/* HEADER HERO SECTION */}
      <header>
        <h1 style={{ 
          color: '#0b3c5d', 
          marginBottom: '15px', 
          fontSize: 'clamp(1.5rem, 6vw, 2.4rem)', 
          fontWeight: '700', 
          lineHeight: '1.3' 
        }}>
          Pre-Travel Doctor Consultation for Medical Tourism: Get an Independent Medical Opinion
        </h1>
        <p style={{ color: '#444', lineHeight: '1.8', fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)', marginBottom: '25px' }}>
          Before booking an international flight for complex cardiovascular surgery, complex multi-joint orthopedic replacements, or comprehensive oncology therapies, scheduling a preliminary medical evaluation is a vital safety benchmark. Securing a remote clinical evaluation allows international patients to verify initial diagnoses, explore alternative treatment methodologies, and gather estimated healing timelines beforehand. 
          By utilizing open research platforms and independent informational directories like <strong>Medical Trip World</strong>, global healthcare travelers can learn how to organize their records, prepare diagnostic files, and cross-reference clinical opinions safely before setting foot abroad.
        </p>
      </header>

      {/* CALL TO ACTION BUTTON */}
      <div style={{ marginBottom: '35px' }}>
        <Link to="/compare-costs" style={{ 
          background: '#28a745', 
          color: '#fff', 
          padding: '14px 24px', 
          textDecoration: 'none', 
          borderRadius: '4px', 
          fontWeight: 'bold', 
          display: 'inline-block', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '0.95rem',
          textAlign: 'center',
          boxSizing: 'border-box',
          maxWidth: '100%'
        }}>
          Access Free Pre-Travel Clinical Checklist Tools →
        </Link>
      </div>

      {/* SECTION 1: THE CRITICAL VALVE OF A SECOND OPINION */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Why an Independent Medical Opinion is Vital Before Medical Travel
        </h2>
        <p style={{ color: '#555', lineHeight: '1.7', marginBottom: '15px', fontSize: '0.95rem' }}>
          A cross-border clinical review acts as an essential security layer for any patient considering tertiary treatments away from home. Remote evaluations help minimize diagnostic disparities, optimize surgical treatment strategies, and clear up localized discrepancies in health system terminology.
        </p>
        <p style={{ color: '#555', lineHeight: '1.7', margin: 0, fontSize: '0.95rem' }}>
          Gathering second opinions helps ensure that a patient's underlying health profiles—such as cardiac parameters or bone density metrics—truly align with the criteria required for invasive surgery. Furthermore, standard global guidance dictates that patients compile their primary laboratory data to confirm that minimally invasive options (like clear angioplasty stenting or advanced arthroscopic techniques) haven't been overlooked in favor of more expensive open surgeries.
        </p>
      </section>

      {/* SECTION 2: FILE PREPARATION MATRIX */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          How to Prepare Your Files for an Overseas Clinical Review
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          When organizing your medical history files to share with international medical evaluation wings, standard data management timelines generally emphasize preparing the following foundational records:
        </p>

        <div style={{ background: '#f4f8fb', padding: 'clamp(15px, 4vw, 20px)', margin: '25px 0', borderRadius: '4px', borderLeft: '4px solid #005ba1', boxSizing: 'border-box' }}>
          <h3 style={{ marginTop: '0', color: '#005ba1', fontSize: '1.2rem', marginBottom: '10px' }}>📋 Mandatory Health Record Document Profiles:</h3>
          <ul style={{ marginTop: '10px', listStyleType: 'square', paddingLeft: '20px', lineHeight: '1.8', color: '#333', fontSize: '0.95rem' }}>
            <li><strong>High-Resolution Diagnostic Imaging:</strong> Gather premium digital copies of recent MRI scans, CT scans, X-rays, or specialized angiography CDs formatted in standard universal file profiles (such as DICOM).</li>
            <li><strong>Comprehensive Biopsy & Blood Panels:</strong> Ensure recent histopathology summaries, cellular biopsy breakdowns, full blood counts, and complete metabolic registries are converted into clear English.</li>
            <li><strong>Active Pharmaceutical Lists:</strong> Detail all current prescription coordinates, blood thinners, insulin ranges, or localized therapies to help foreign teams cross-check toxicological reactions.</li>
            <li><strong>Summary of Clinical History:</strong> Maintain a chronological, typed index tracking previous hospital admissions, surgical histories, known allergic triggers, and current baseline limitations.</li>
          </ul>
        </div>
      </section>

      {/* SECTION 3: SAFETY & DELEGATION CHECKPOINTS - ADAPTIVE GRID FOR PHONES */}
      <section style={{ marginBottom: '35px' }}>
        <h2 style={{ color: '#0b3c5d', borderBottom: '2px solid #f4f8fb', paddingBottom: '8px', fontSize: 'clamp(1.2rem, 5vw, 1.5rem)' }}>
          Verifying Specialist Backgrounds & Communication Pathways
        </h2>
        <p style={{ color: '#555', lineHeight: '1.6', fontSize: '0.95rem' }}>
          Because Medical Trip World operates exclusively as an open informational venue and independent directory reference, we strongly urge international families to self-verify specialists directly. Ensure your chosen evaluation pathways fulfill the following quality standards:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginTop: '15px' }}>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Active Board Certifications</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Confirm that reviewing specialists hold active national registry status and maintain robust clinical portfolios. Verified centers readily outline senior consultant logs showing decades of international patient treatment experience.
            </p>
          </div>
          <div style={{ padding: '15px', background: '#fafafa', borderRadius: '4px', border: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#0b3c5d', fontSize: '1.05rem' }}>Telemedicine Security Measures</h4>
            <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              Verify that virtual consulting portals deploy encrypted video links and encrypted cloud repositories to secure sensitive health records, completely aligning with global digital patient safety acts.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: IN-DEPTH FAQ */}
      <section style={{ marginBottom: '35px', background: '#fff', border: '1px solid #e0e0e0', padding: 'clamp(15px, 4vw, 20px)', borderRadius: '4px' }}>
        <h3 style={{ color: '#0b3c5d', marginTop: '0', marginBottom: '20px', fontSize: '1.25rem' }}>
          Frequently Asked Questions (FAQs) – Pre-Travel Clinical Consultation
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Can a remote doctor consultation replace an in-person physical evaluation?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            No. A remote digital review serves purely as a preliminary advisory evaluation to review existing lab work, outline options, and discuss expected cost brackets. Finalized clinical decisions and operating clearances are determined exclusively after comprehensive, on-site physical diagnostic testing at the treating hospital.
          </p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>What is the typical turnaround timeline to receive an international opinion report?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            Once comprehensive medical records and translated clinical inputs are successfully shared with an international facility's review wing, a specialized opinion or cost-bracket analysis is typically returned within 48 to 72 business hours.
          </p>
        </div>
        
        <div style={{ marginBottom: '5px' }}>
          <p style={{ fontWeight: 'bold', color: '#333', margin: '0 0 5px 0', fontSize: '0.95rem' }}>Does Medical Trip World employ doctors or issue official medical opinions?</p>
          <p style={{ color: '#666', margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
            No. Medical Trip World is an independent informational guide and directory portal. We do not provide medical treatments, maintain an internal staff of consulting doctors, or issue clinical diagnoses. Our tools assist you in structuring data parameters; all medical consultations, definitive reviews, and subsequent care contracts take place directly between the patient and their independently selected healthcare institution.
          </p>
        </div>
      </section>

      {/* RE-ENGAGEMENT FOOTER */}
      <footer style={{ borderTop: '1px solid #eee', paddingTop: '20px', textAlign: 'center' }}>
        <p style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#333', margin: '0 0 15px 0' }}>Begin Compiling Your Medical Travel Research Safely</p>
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <style>{`
            @media (max-width: 550px) {
              .footer-btn { width: 100% !important; text-align: center; box-sizing: border-box; }
            }
          `}</style>
          <Link to="/submit-query" className="footer-btn" style={{ background: '#28a745', color: '#fff', padding: '12px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            Submit General Information Request
          </Link>
          <Link to="/compare-costs" className="footer-btn" style={{ background: '#fff', color: '#0b3c5d', border: '2px solid #0b3c5d', padding: '10px 20px', textDecoration: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', display: 'inline-block' }}>
            View Estimated Cost Charts
          </Link>
        </div>
      </footer>

    </article>
  );
}

export default DoctorConsultation;